// src/pages/AccountDeletion.js
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './AccountDeletion.css';

const AccountDeletion = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Suppression de compte | Gates of Music</title>
        <meta 
          name="description" 
          content="Demandez la suppression de votre compte Gates of Music et de toutes vos données personnelles. Procédure simple et rapide." 
        />
        <link rel="canonical" href="https://www.gatesofmusic.com/account-deletion" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="account-deletion-page">
        <Header />
        
        <main className="deletion-content">
          <div className="deletion-container">
            
            {/* En-tête */}
            <header className="deletion-header">
              <div className="header-icon">🗑️</div>
              <h1>Suppression de compte</h1>
              <p className="header-subtitle">Gates of Music</p>
            </header>

            {/* Introduction */}
            <section className="deletion-section intro-section">
              <p className="intro-text">
                Nous respectons votre droit à supprimer votre compte et vos données personnelles de l'application 
                <strong> Gates of Music</strong>. Cette page explique comment demander la suppression de votre compte 
                et ce qui se passera avec vos données.
              </p>
            </section>

            {/* Procédure de suppression */}
            <section className="deletion-section">
              <h2>📋 Comment supprimer votre compte</h2>
              
              <div className="steps-container">
                <div className="step-card">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h3>Envoyez un email</h3>
                    <p>Envoyez une demande de suppression à :</p>
                    <a href="mailto:support@gatesofmusic.com?subject=Demande de suppression de compte" className="email-link">
                      contact@gatesofmusic.com
                    </a>
                    <p className="step-note">
                      <strong>Objet :</strong> "Demande de suppression de compte"
                    </p>
                  </div>
                </div>

                <div className="step-card">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h3>Informations à fournir</h3>
                    <p>Dans votre email, incluez :</p>
                    <ul>
                      <li>Votre adresse email utilisée pour l'inscription</li>
                      <li>Votre nom complet</li>
                      <li>Confirmation explicite : "Je demande la suppression de mon compte"</li>
                    </ul>
                  </div>
                </div>

                <div className="step-card">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h3>Confirmation</h3>
                    <p>Nous vous répondrons sous <strong>48 heures</strong> pour :</p>
                    <ul>
                      <li>Confirmer la réception de votre demande</li>
                      <li>Vérifier votre identité si nécessaire</li>
                      <li>Vous informer de la suppression effective</li>
                    </ul>
                  </div>
                </div>

                <div className="step-card">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h3>Suppression effective</h3>
                    <p>Votre compte sera supprimé sous <strong>30 jours</strong> maximum après validation de votre demande.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Suppression depuis l'application */}
            <section className="deletion-section">
              <h2>📱 Suppression depuis l'application</h2>
              
              <div className="app-deletion-box">
                <p>Vous pouvez également supprimer votre compte directement depuis l'application :</p>
                
                <ol className="app-steps">
                  <li>Ouvrez l'application <strong>Gates of Music</strong></li>
                  <li>Accédez à <strong>Mon Profil</strong></li>
                  <li>Appuyez sur <strong>Paramètres</strong></li>
                  <li>Sélectionnez <strong>"Supprimer mon compte"</strong></li>
                  <li>Confirmez votre choix</li>
                </ol>

                <p className="warning-text">
                  ⚠️ <strong>Attention :</strong> Cette action est irréversible. Toutes vos données seront définitivement supprimées.
                </p>
              </div>
            </section>

            {/* Données supprimées */}
            <section className="deletion-section">
              <h2>🗑️ Données qui seront supprimées</h2>
              
              <div className="data-grid">
                <div className="data-card deleted">
                  <div className="data-icon">✅</div>
                  <h3>Supprimées définitivement</h3>
                  <ul>
                    <li>Informations de compte (nom, email, téléphone)</li>
                    <li>Photo de profil et informations personnelles</li>
                    <li>Historique de recherches et préférences</li>
                    <li>Messages et communications</li>
                    <li>Avis et commentaires laissés</li>
                    <li>Données de localisation</li>
                    <li>Informations de connexion</li>
                  </ul>
                </div>

                <div className="data-card retained">
                  <div className="data-icon">📦</div>
                  <h3>Conservées temporairement</h3>
                  <ul>
                    <li><strong>Logs de sécurité :</strong> 1 an (prévention de la fraude)</li>
                    <li><strong>Données analytiques anonymisées :</strong> Indéfiniment (pas d'identification personnelle)</li>
                  </ul>
                  <p className="retention-note">
                    Ces données sont conservées uniquement pour respecter nos obligations légales et réglementaires.
                  </p>
                </div>
              </div>
            </section>

            {/* Conséquences */}
            <section className="deletion-section">
              <h2>⚠️ Conséquences de la suppression</h2>
              
              <div className="consequences-box">
                <p>Une fois votre compte supprimé :</p>
                <ul className="consequences-list">
                  <li>❌ Vous ne pourrez plus vous connecter à l'application</li>
                  <li>❌ Toutes vos réservations en cours seront annulées</li>
                  <li>❌ Vous perdrez l'accès à votre historique</li>
                  <li>❌ Vos avis et commentaires seront supprimés</li>
                  <li>❌ Les prestataires ne pourront plus vous contacter</li>
                  <li>✅ Vous pourrez créer un nouveau compte avec la même adresse email après 30 jours</li>
                </ul>
              </div>
            </section>

            {/* Alternatives */}
            <section className="deletion-section">
              <h2>💡 Alternatives à la suppression</h2>
              
              <div className="alternatives-grid">
                <div className="alternative-card">
                  <div className="alternative-icon">⏸️</div>
                  <h3>Désactiver temporairement</h3>
                  <p>
                    Vous pouvez désactiver votre compte temporairement sans le supprimer. 
                    Vos données seront conservées et vous pourrez réactiver votre compte à tout moment.
                  </p>
                  <Link to="/auth" className="alternative-link">Se connecter pour désactiver</Link>
                </div>

                <div className="alternative-card">
                  <div className="alternative-icon">🔒</div>
                  <h3>Modifier vos préférences de confidentialité</h3>
                  <p>
                    Vous pouvez limiter le partage de vos données ou modifier vos préférences 
                    de communication sans supprimer votre compte.
                  </p>
                  <Link to="/mon-profil" className="alternative-link">Gérer mes paramètres</Link>
                </div>
              </div>
            </section>

            {/* Délai de traitement */}
            <section className="deletion-section">
              <h2>⏱️ Délai de traitement</h2>
              
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>Jour 0</h4>
                    <p>Vous envoyez votre demande de suppression</p>
                  </div>
                </div>

                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>Sous 48h</h4>
                    <p>Nous confirmons la réception et vérifions votre identité</p>
                  </div>
                </div>

                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>Sous 7 jours</h4>
                    <p>Votre compte est désactivé (vous ne pouvez plus vous connecter)</p>
                  </div>
                </div>

                <div className="timeline-item">
                  <div className="timeline-dot active"></div>
                  <div className="timeline-content">
                    <h4>Sous 30 jours</h4>
                    <p>Suppression définitive de toutes vos données personnelles</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="deletion-section">
              <h2>📞 Besoin d'aide ?</h2>
              
              <div className="contact-box">
                <p>Si vous avez des questions concernant la suppression de votre compte :</p>
                
                <div className="contact-methods">
                  <div className="contact-method">
                    <span className="method-icon">📧</span>
                    <div>
                      <strong>Email :</strong>
                      <a href="mailto:support@gatesofmusic.com">contact@gatesofmusic.com</a>
                    </div>
                  </div>

                  <div className="contact-method">
                    <span className="method-icon">📞</span>
                    <div>
                      <strong>Téléphone :</strong>
                      <a href="tel:+212123456789">+212 6 24 38 13 11</a>
                      <span className="hours">(Lun-Ven, 9h-18h)</span>
                    </div>
                  </div>
                </div>

                <p className="response-time">
                  ⏱️ Temps de réponse moyen : <strong>24 heures</strong>
                </p>
              </div>
            </section>

            {/* Footer CTA */}
            <div className="deletion-footer">
              <p>
                Consultez notre <Link to="/privacy-policy" className="footer-link">Politique de Confidentialité</Link> 
                {' '}pour plus d'informations sur la gestion de vos données personnelles.
              </p>
              
              <a 
                href="mailto:support@gatesofmusic.com?subject=Demande de suppression de compte&body=Bonjour,%0D%0A%0D%0AJe souhaite supprimer mon compte Gates of Music.%0D%0A%0D%0AEmail de mon compte : %0D%0ANom complet : %0D%0A%0D%0AJe confirme vouloir supprimer définitivement mon compte et toutes mes données associées.%0D%0A%0D%0AMerci." 
                className="deletion-cta-button"
              >
                📧 Envoyer une demande de suppression
              </a>
            </div>

          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AccountDeletion;